// LRPO-5811 — the "Notify this territory's reps when a scanned lead matches: …"
// sentence is rendered server-side from the SAVED rows, so the moment a
// background autosave succeeded it described the territory as it was BEFORE the
// edit, and stayed wrong until the page was reloaded.
//
// ★ THE SERVER SENDS THE HTML; THIS CONTROLLER ONLY SWAPS IT IN. The sentence is
// a RULE in prose — groups OR'd, criteria types within a group AND'ed, values
// within a type OR'd, place names summarised after five. Rebuilding that here
// would be a second implementation of the rule, and the two would drift the first
// time the wording changed.
//
// ⚠ innerHTML IS DELIBERATE, against the usual preference for textContent. The
// payload is not a server VALUE, it is the rendered partial, and it has to keep
// its <strong>, <ol> and <li> or the sentence loses the grouping it exists to
// show. Every interpolation inside that partial goes through ERB's `<%= %>`, so
// user-supplied company and demographic names are already escaped.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    // LRPO-5826 — this surface is DISPLAY ONLY and is rewritten by the OUTCOME of
    // a save, so it must not look like an edit to territory_autosave_controller's
    // MutationObserver. Without this, every successful save swaps the innerHTML
    // below, the observer sees a real mutation inside #exhibitor-form and
    // schedules the next save: a full-form POST every 3 seconds, forever, with
    // nobody at the keyboard.
    //
    // Declared HERE rather than on the div in _territory_area.html.erb so that
    // any future use of data-controller="territory-sentence" is covered, and so
    // an ERB edit cannot silently reintroduce the loop. This is the same
    // `[data-autosave-ignore]` contract the status label and notice slot use.
    this.element.setAttribute("data-autosave-ignore", "")

    this._onAutosaved = (event) => this.replace(event.detail)
    document.addEventListener("territory:autosaved", this._onAutosaved)
  }

  disconnect() {
    document.removeEventListener("territory:autosaved", this._onAutosaved)
  }

  replace(detail) {
    // ⚠ NOT EVERY territory:autosaved CARRIES THE SENTENCE. The event is a shared
    // contract also used by the location pickers (LRPO-5640) and the notice
    // banner (LRPO-5669), and a partial-render failure returns the payload with
    // the key absent. Blanking the sentence then would be WORSE than leaving it
    // stale — it would tell the exhibitor the territory matches nothing.
    const html = detail && detail.territory_sentence_html
    if (!html) return

    // ⚠ THE SERVER RENDERS EVERY DISCLOSURE CLOSED, so the user's choice to
    // expand a "3 more" lives ONLY in the DOM — and the DOM is exactly what
    // the swap below replaces. Lift the open ones' keys out first, put them
    // back after.
    //
    // The key is the term's LABEL, not its position: a save can insert a whole
    // new term row above an existing one, and the row the user opened must
    // come back open while the new row stays closed.
    const openKeys = new Set()
    for (const more of this.element.querySelectorAll(".territory-sentence__more")) {
      const hidden = more.querySelector(".territory-sentence__hidden")
      if (hidden && hidden.hidden === false) openKeys.add(this.disclosureKey(more))
    }

    this.element.innerHTML = html

    for (const more of this.element.querySelectorAll(".territory-sentence__more")) {
      if (!openKeys.has(this.disclosureKey(more))) continue
      const hidden = more.querySelector(".territory-sentence__hidden")
      const button = more.querySelector(".territory-sentence__more-button")
      if (!hidden || !button) continue
      hidden.hidden = false
      // Assigning an absent data attribute would BLANK the button — the DOM turns
      // `textContent = undefined` into an empty string, leaving a control with no
      // text. Keep whatever the server rendered instead.
      if (button.dataset.lessLabel) button.textContent = button.dataset.lessLabel
      button.setAttribute("aria-expanded", "true")
    }
  }

  // Key for a .territory-sentence__more disclosure: [ul index] + [term label]
  // + [how many earlier disclosures in that ul already had the label], so two
  // terms sharing a label still get distinct keys. The label is user data and
  // is only ever COMPARED as a string, never put into a selector.
  disclosureKey(more) {
    const ul = more.closest("ul.territory-sentence__terms")
    const ulIndex = ul
      ? Array.prototype.indexOf.call(
          this.element.querySelectorAll("ul.territory-sentence__terms"),
          ul
        )
      : -1

    const li = more.closest("li.territory-sentence__term")
    const labelEl = li && li.querySelector(".territory-sentence__term-label")
    const label = labelEl ? labelEl.textContent.trim() : ""

    let occurrence = 0
    if (ul) {
      for (const sibling of ul.querySelectorAll(".territory-sentence__more")) {
        if (sibling === more) break
        const sibLi = sibling.closest("li.territory-sentence__term")
        const sibLabelEl = sibLi && sibLi.querySelector(".territory-sentence__term-label")
        const sibLabel = sibLabelEl ? sibLabelEl.textContent.trim() : ""
        if (sibLabel === label) occurrence++
      }
    }

    // \u0000 because the three parts are glued into one string and a label can
    // contain anything an exhibitor typed. Joined bare, group 1's "0A" and group
    // 10's "A" are the same key, and the restore opens the wrong row.
    return [ulIndex, label, occurrence].join("\u0000")
  }
}
